import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useLabelCountLimit } from '@/composables/map/UseLabelCountLimit.js'
import _ from 'lodash'
import log from 'loglevel'
import {
  MapLayerMVTRenderer,
  MapLayerStyleConverter,
  RestApiClient,
  getNorthEnd,
  isFilterTarget,
} from 'ifield-core-components'
import store from '@/store'
import { MAX_SAFE_INTERVAL_DELAY } from '@/constants.js'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import {
  getPlaceItemIcon,
  getPlaceItemText,
} from '@/js/util/PlaceLabelItemText.js'
import {
  PLACE_DISP_ITEM_KEY,
  buildLabelLayerSettings,
  buildMapLayerByPlaceGroupId,
  findPlaceMapLayer,
  findPlaceMapLayers,
  isLabelLayerVisible,
  isLabelVisibleAtZoom,
  toLabelLayerKey,
} from '@/js/util/PlaceLabelUtil.js'
import {
  EMPTY_ITEM_TEXT,
  buildLabelText,
  findLabelItem,
  toRgb,
} from '@/js/util/UserLabelUtil.js'

/**
 * 地図画面のプレイスラベル（吹き出し）に出す値を取得して、描画データに組み立てる。
 *
 * 2026-08-20 の打ち合わせで決まったこと（決定1-1／1-2／1-3）:
 *  - 値はユーザー側と同じ「追加項目」のパラメータをプレイスのAPIに用意して返してもらう
 *  - 取得はポーリング。間隔は個人設定（30〜300秒・既定60秒）
 *  - 2段のラベルを出すのは地図画面だけ
 *
 * ラベルを出す条件は、管理画面で**プレイスリストの表示項目が設定されていること**。
 * 設定が無い企業では従来どおりタイル側のラベル（プレイス名だけ）が出る
 */

/**
 * 1回の取得で読むプレイスの上限
 *
 * 決定2-2で「視認性の観点から数百件」が上限と決まっている。
 * 上限に達した場合は取りこぼしがあるため、黙って切らずにログへ残す
 * @type {Number}
 */
const PLACE_FETCH_LIMIT = 1000

/**
 * プレイスの更新間隔の既定値（秒）
 *
 * 個人設定の項目（サーバー側で追加予定）が無い間はこの値を使う
 * @type {Number}
 */
const DEFAULT_UPDATE_INTERVAL_SEC = 60

/**
 * 表示範囲の変更から取得までの待ち時間(ms)
 * 地図を動かしている間に何度も取りに行かないようにする
 * @type {Number}
 */
const FETCH_DEBOUNCE_MS = 500

/**
 * ラベルの既定のスタイル（マップレイヤーのスタイルが引けなかった場合）
 * @type {Object}
 */
const DEFAULT_LABEL_STYLE = {
  fontSize: 13,
  textColor: [51, 51, 51],
  backgroundColor: [255, 255, 255, 255],
  iconColor: [51, 51, 51, 255],
  pointOffset: [0, 0],
}

/**
 * マーカー画像が読み込まれていない場合に使うマーカー情報
 * 共通ライブラリのスタイル変換が `originalMapping` を直接読むため、空でも形を渡す
 * @type {Object}
 */
const EMPTY_MARKER = { originalMapping: { height: 0, anchorY: 0 } }

/**
 * 表示範囲をAPIのbboxパラメータにする
 * @param {Object} bounds 地図の表示範囲({ sw: [lng, lat], ne: [lng, lat] })
 * @returns {String|null} bboxパラメータ
 */
const toBbox = (bounds) => {
  if (!Array.isArray(bounds?.sw) || !Array.isArray(bounds?.ne)) return null
  const [west, south] = bounds.sw
  const [east, north] = bounds.ne
  return `${west},${south},${east},${north}`
}

/**
 * 座標が表示範囲の中にあるか
 *
 * ユーザー側の件数の数え方（`TheMember.vue` の `$_countUserLabelsInView`）と揃えている
 * @param {Array.<Number>} position 座標([lng, lat])
 * @param {Object} bounds 地図の表示範囲({ sw: [lng, lat], ne: [lng, lat] })
 * @returns {Boolean} 範囲内ならtrue
 */
const isInBounds = (position, bounds) => {
  if (!Array.isArray(bounds?.sw) || !Array.isArray(bounds?.ne)) return false
  const [west, south] = bounds.sw
  const [east, north] = bounds.ne
  const [lng, lat] = position
  if (lat < south || lat > north) return false
  // 日付変更線をまたぐ表示範囲では東西の大小が入れ替わる
  return west <= east ? lng >= west && lng <= east : lng >= west || lng <= east
}

/**
 * マップレイヤーのラベルスタイルを、ラベルレンダラーが使う形に直す
 *
 * 色・文字サイズ・ピンの上に出すずらし量の算出は、判定を二重に持たないよう
 * 共通ライブラリの `MapLayerStyleConverter` に任せる
 * @param {Object} layer マップレイヤー
 * @param {Map} markerImageStore マーカー画像ストア
 * @returns {Object} スタイル
 */
const toLabelStyle = (layer, markerImageStore) => {
  const marker = markerImageStore?.get(layer.marker_path) || EMPTY_MARKER
  const label = MapLayerStyleConverter.convertToLabelStyle(layer, marker)
  const textColor = toRgb(label.textColor) || DEFAULT_LABEL_STYLE.textColor
  const fill = toRgb(label.fill)
  return {
    fontSize: label.fontSize || DEFAULT_LABEL_STYLE.fontSize,
    textColor,
    backgroundColor: fill
      ? [...fill, 255]
      : DEFAULT_LABEL_STYLE.backgroundColor,
    iconColor: [...textColor, 255],
    pointOffset: label.pointOffset || DEFAULT_LABEL_STYLE.pointOffset,
  }
}

/**
 * プレイスラベルの値の取得と描画データの組み立て
 * @param {Ref.<Object>} mapBounds 地図の表示範囲
 * @param {Ref.<Array.<Object>>} renderParameters 地図画面のレイヤー描画パラメータ
 * @returns {Object} プレイスラベルの状態
 */
export function usePlaceLabel({ mapBounds, renderParameters }) {
  /**
   * 取得したプレイス
   * @type {Ref.<Array.<Object>>}
   */
  const places = shallowRef([])
  /**
   * 取得の世代
   *
   * 広い範囲の取得は遅いため、寄ったあとに古い結果が届くことがある。
   * 自分より新しい取得が始まっていたら、その結果で上書きしない
   * @type {Number}
   */
  let fetchGeneration = 0
  /**
   * エラー表示済みフラグ（同じ失敗を繰り返し出さない）
   * @type {Ref.<Boolean>}
   */
  const isErrorShown = ref(false)
  /**
   * インターバルID（非リアクティブ）
   * @type {Number}
   */
  let intervalId

  // エラーハンドリング関数展開
  const { handleApiError } = useHandleApiError()

  /**
   * ラベルの2段目に出す表示項目
   *
   * 管理画面で設定した並びの先頭。利用者の一覧の操作では変わらない（決定4）
   * @type {ComputedRef.<String|null>}
   */
  const dispItem = computed(() => {
    const itemValue =
      store.getters['systemSetting/getItemValueByItemKey'](PLACE_DISP_ITEM_KEY)
    return itemValue?.split('|').filter(Boolean)[0] || null
  })
  /**
   * マップレイヤーのマスタ
   * @type {ComputedRef.<Array.<Object>>}
   */
  const mapLayers = computed(() => store.getters['mapLayer/mapLayers'])
  /**
   * マーカー画像ストア（ピンの高さぶんラベルを持ち上げるために使う）
   * @type {ComputedRef.<Map>}
   */
  const markerImageStore = computed(
    () => store.getters['mapLayer/markerImageStore']
  )
  /**
   * 現在のズームレベル
   * @type {ComputedRef.<Number>}
   */
  const zoom = computed(() => store.getters['map/zoom'])
  /**
   * マップレイヤーごとのラベル表示設定
   * @type {ComputedRef.<Object>}
   */
  const layerSettings = computed(() =>
    buildLabelLayerSettings({
      renderParameters: renderParameters.value,
      mapLayers: mapLayers.value,
      mvtLayerId: MapLayerMVTRenderer.layerId,
    })
  )
  /**
   * 今のズームレベルでラベルを出すレイヤーが1つでもある場合はtrue
   *
   * プレイスのラベルは管理画面のラベルズーム範囲で出し分けられており、
   * 1枚も出ない状態で値を取りに行っても使い道が無い
   * @type {ComputedRef.<Boolean>}
   */
  const hasVisibleLabelLayer = computed(() =>
    Object.values(layerSettings.value).some(
      (setting) =>
        setting.visible &&
        isLabelVisibleAtZoom(
          zoom.value,
          setting.labelMinZoom,
          setting.labelMaxZoom
        )
    )
  )
  /**
   * 表示中のフロアID（屋内表示中でなければ null）
   * @type {ComputedRef.<Number|null>}
   */
  const activeIndoorLevelId = computed(
    () => store.state.mapIndoor?.activeIndoorLevelId || null
  )
  /**
   * プレイスラベルを画面側で描く設定になっている場合はtrue
   *
   * 表示項目が設定されていない企業では、従来どおりタイル側のラベルを出す。
   * 2段目を出すかどうかは個人設定（既定OFF・決定3-1）で、OFFのときも従来どおりになる。
   * **件数による打ち切りはここに含めない。** 含めると、件数を数えるための取得まで
   * 止まってしまい、件数が減っても戻れなくなる
   * @type {ComputedRef.<Boolean>}
   */
  const isLabelConfigured = computed(
    () => !!dispItem.value && !!store.state.labelSecondLine?.place
  )
  /**
   * 値を取りに行く場合はtrue
   * @type {ComputedRef.<Boolean>}
   */
  const isFetchable = computed(
    () => isLabelConfigured.value && hasVisibleLabelLayer.value
  )
  /**
   * 自動更新の間隔(ms)
   * @type {ComputedRef.<Number>}
   */
  const intervalMs = computed(() => {
    const seconds =
      store.state.placeLabel?.updateInterval || DEFAULT_UPDATE_INTERVAL_SEC
    return Math.min(seconds * 1000, MAX_SAFE_INTERVAL_DELAY)
  })
  /**
   * プレイスグループIDからマップレイヤーを引く表
   * @type {ComputedRef.<Map.<Number, Object>>}
   */
  const mapLayerByPlaceGroupId = computed(() =>
    buildMapLayerByPlaceGroupId(findPlaceMapLayers(mapLayers.value))
  )
  /**
   * マップレイヤーごとのラベルスタイル
   * 地物ごとに作り直さないよう、レイヤー単位で1回だけ変換する
   * @type {ComputedRef.<Map.<Number, Object>>}
   */
  const styleByMapLayerId = computed(
    () =>
      new Map(
        findPlaceMapLayers(mapLayers.value).map((layer) => [
          layer.id,
          toLabelStyle(layer, markerImageStore.value),
        ])
      )
  )
  /**
   * ラベルの表示位置を返す
   * 既存のタイル側のラベルと同じ「図形の最も北の点」に合わせる
   * @param {Object} place プレイス
   * @returns {Array.<Number>|null} 座標
   */
  const getLabelPosition = (place) => {
    const geometry = place?.geom?.geometry
    if (!geometry) return null
    const point = getNorthEnd(geometry)
    return Array.isArray(point) && point.length >= 2 ? point : null
  }

  /**
   * ラベルを出せるプレイス
   *
   * **2段目を出しているかどうかに依存させない。** 描画結果から件数を数えると
   * 「止める→0件→戻す→止める」の循環になる
   * @type {ComputedRef.<Array.<Object>>}
   */
  const labelTargets = computed(() => {
    const targets = []
    for (const place of places.value) {
      // タイルはマップレイヤー単位で描かれるので、レイヤーに属さないプレイスは地図に出ない
      const layer = findPlaceMapLayer(place, mapLayerByPlaceGroupId.value)
      if (!layer) continue
      const position = getLabelPosition(place)
      if (!position) continue
      targets.push({
        place,
        layer,
        position,
        geometryType: place?.geom?.geometry?.type,
        layerKey: toLabelLayerKey(layer.id),
        // 地物の絞り込みは既存実装と同じく properties を見て判定する
        properties: {
          place_id: place.id,
          indoor_level_id: place.indoor_level_id,
        },
      })
    }
    return targets
  })
  /**
   * 実際に地図に出るラベル
   *
   * プレイス一覧APIは表示範囲のプレイスを地図の表示状態に関係なく返すので、
   * ここでタイル側と同じ絞り込みを掛ける（IFIELD_V4-7280）。
   *
   * - レイヤーメニューで消したマップレイヤーのラベルは出ない
   * - マップレイヤーのラベル表示ズーム範囲から外れたラベルは出ない
   * - 地物の絞り込み（屋内のフロア・一覧での絞り込み）から外れたラベルは出ない
   *
   * 屋内表示中はフロアの絞り込みが効いていて、そのフロアのプレイスしか地図に出ない。
   * 絞り込む前の件数で判定すると、ズームアウトして屋外のプレイスが表示範囲に入っただけで
   * 「プレイスが多い」と知らせてしまう
   * @type {ComputedRef.<Array.<Object>>}
   */
  const drawableTargets = computed(() => {
    const settings = layerSettings.value
    const currentZoom = zoom.value
    // 同じマップレイヤーの判定を地物の数だけ繰り返さない
    const visibleByKey = new Map()
    return labelTargets.value.filter((target) => {
      const key = target.layerKey
      let visible = visibleByKey.get(key)
      if (visible === undefined) {
        visible = isLabelLayerVisible(settings[key], currentZoom)
        visibleByKey.set(key, visible)
      }
      if (!visible) return false
      const filter = settings[key]?.filter
      return !filter || isFilterTarget(target, filter)
    })
  })
  /**
   * 画面に出るラベルの件数
   *
   * **いま見えている範囲に入るものだけを数える**（2026-08-26 合意の「画面内に出るラベルの数」）。
   * 取得済みの一覧には表示範囲が変わる前のものが残っていることがあり
   * （広い範囲の取得は遅いため、寄ったあとに完了する）、それを数えると
   * 1件のプレイスに寄った直後に「プレイスが多い」と知らせてしまう
   * @type {ComputedRef.<Number>}
   */
  const labelCount = computed(
    () =>
      drawableTargets.value.filter((target) =>
        isInBounds(target.position, mapBounds.value)
      ).length
  )
  const { isOverLimit } = useLabelCountLimit(labelCount)
  /**
   * プレイスラベルを画面側で描く場合はtrue
   *
   * 件数が多すぎるときは2段目を出さず、従来どおりタイル側の1行ラベルに戻す（2026-08-26 合意）
   * @type {ComputedRef.<Boolean>}
   */
  const isEnabled = computed(
    () => isLabelConfigured.value && !isOverLimit.value
  )

  /**
   * ラベルの描画データ
   *
   * 地図に出ないラベルは組み立てない（文字とスタイルを作るぶんだけ無駄になる）
   * @type {ComputedRef.<Array.<Object>>}
   */
  const placeLabelData = computed(() => {
    if (!isEnabled.value) return []
    const item = dispItem.value
    const data = []
    for (const target of drawableTargets.value) {
      const { place, layer, position, geometryType } = target
      const value = findLabelItem(place, item)
      // 値が無くても2段目は出す（決定3-2）
      const itemText = getPlaceItemText(value, place) || EMPTY_ITEM_TEXT
      data.push({
        position,
        // ラベルを持ち上げるかどうかは図形の種別で決まる（`getLabelPixelOffset`）
        geometryType,
        text: buildLabelText(place.place_name, itemText),
        icon: getPlaceItemIcon(value),
        style: styleByMapLayerId.value.get(layer.id) || DEFAULT_LABEL_STYLE,
        layerKey: target.layerKey,
        properties: target.properties,
      })
    }
    return data
  })

  /**
   * プレイスを取得する
   */
  const fetchFromApi = async () => {
    const bbox = toBbox(mapBounds.value)
    if (!bbox) return

    const url = RestApiClient.createApiUrl('place')
    // 値はユーザー側と同じ「追加項目」のパラメータで返る（決定1-1）。
    // 件数が多すぎて2段目を止めている間は値を使わないので、重い問い合わせをさせない。
    // ただし件数が減ったかを知るため、取得そのものは続ける
    if (!isOverLimit.value) {
      url.searchParams.append('additional_items', dispItem.value)
    }
    // 屋内表示中は、そのフロアのプレイスしか地図に出ない。
    // 取得も同じ条件にしないと、地図に出ない屋外のプレイスで取得上限が埋まる
    if (activeIndoorLevelId.value) {
      url.searchParams.append('indoor_level_id', activeIndoorLevelId.value)
    }
    url.searchParams.append('bbox', bbox)
    url.searchParams.append('limit', PLACE_FETCH_LIMIT)
    url.searchParams.append('offset', 0)

    const generation = ++fetchGeneration
    const json = await RestApiClient.executeGetApi(url)
    // 取得中にもっと新しい取得が始まっていたら、古い結果で上書きしない。
    // 表示範囲が変われば取り直すので、捨てても取りこぼしにはならない
    if (generation !== fetchGeneration) return
    places.value = json.data || []
    if (places.value.length >= PLACE_FETCH_LIMIT) {
      log.warn(
        `[WARN] place label: reached the fetch limit (${PLACE_FETCH_LIMIT}). some labels are not shown.`
      )
    }
  }

  /**
   * 自動で取得する
   */
  const fetchAuto = async () => {
    if (!isFetchable.value) return
    try {
      await fetchFromApi()
      isErrorShown.value = false
    } catch (error) {
      // 取得のたびに同じエラーを出さない
      if (!isErrorShown.value) {
        handleApiError(error, () =>
          log.error('[ERROR] failed to fetch place labels', error)
        )
        isErrorShown.value = true
      }
    }
  }

  /**
   * 自動更新を開始する
   */
  const startAutoUpdate = () => {
    stopAutoUpdate()
    if (!isFetchable.value) return
    intervalId = setInterval(fetchAuto, intervalMs.value)
  }

  /**
   * 自動更新を停止する
   */
  const stopAutoUpdate = () => {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = undefined
    }
  }

  /**
   * 表示範囲が変わったら取り直す
   * 地図を動かしている間に何度も取りに行かないよう少し待つ
   */
  const fetchOnBoundsChanged = _.debounce(fetchAuto, FETCH_DEBOUNCE_MS)

  /**
   * 表示項目をサーバーから取得する
   */
  const updateDispItem = async () => {
    try {
      await store.dispatch(
        'systemSetting/updateSettingByItemKey',
        PLACE_DISP_ITEM_KEY
      )
    } catch (error) {
      handleApiError(error, () =>
        log.warn(
          `failed to fetch systemSetting/${PLACE_DISP_ITEM_KEY}. use fetched before.`
        )
      )
    }
  }

  // 取りに行ける状態になったら取得と自動更新を始める
  // 件数超過の切り替わりでも取り直す。
  // 超過中は値を付けずに取っているので、戻ったときに取り直さないと値が空のままになる。
  // フロアを変えたときも取り直す（取得の条件にフロアが入っているため）
  watch(
    [isFetchable, intervalMs, isOverLimit, activeIndoorLevelId],
    ([fetchable]) => {
      if (fetchable) {
        fetchAuto()
        startAutoUpdate()
      } else {
        stopAutoUpdate()
        places.value = []
      }
    },
    { immediate: true }
  )
  // 表示範囲が変わったら取り直す（タイルと同じく画面内のぶんだけ持つ）
  watch(() => toBbox(mapBounds.value), fetchOnBoundsChanged)

  // unmount 時に自動更新は停止
  onBeforeUnmount(() => {
    fetchOnBoundsChanged.cancel()
    stopAutoUpdate()
  })

  updateDispItem()

  return {
    isPlaceLabelEnabled: isEnabled,
    isPlaceLabelOverLimit: isOverLimit,
    placeLabelCount: labelCount,
    placeLabelData,
    fetchPlaceLabels: fetchAuto,
  }
}
